/**
 * The entity layer of a search (docs/entity-layer.md). Above the answer, a strip per data model of your own that the
 * search names something in: "Customer · Vantaan Kiinteistöhuolto Oy — 3 systems · 41 documents · 2 differences → Open".
 * Additive: a search that names nothing in your models shows no strip and the stage stays as it is. The window lays the
 * entity out field by field (rows) and system by system (columns), the values the systems disagree on marked, with each
 * system's latest update; the previous and next entity the search found are a step away. It runs the version of the
 * model deployed to the environment, so publishing and deploying a model is what changes it here.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import * as Dialog from '@radix-ui/react-dialog';
import { Api, type EntityLayer, type EntityMatch, type LensEntity } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useEnv } from '../lib/envs.tsx';
import { useSearchParams } from '../lib/liveparams.ts';
import { useUrlModal } from '../lib/urlmodal.ts';
import { ago, count, dateTime } from '../lib/format.ts';
import { entitySearch } from '../lib/models.ts';
import { ENTITY_KEY, ROW_FILTERS, encodeEntity, entityMatrix, entityRows, entitySummary, pluralModel, readEntity, readEntityView, rowMatches, sameKey, writeEntity, writeEntityView, type EntityRow, type RowFilter } from '../lib/entities.ts';
import { FacetChip, Segmented } from './FilterChips.tsx';
import { highlightParts } from '../lib/highlight.ts';
import { IconButton, SystemBadge, cx } from '../design/primitives.tsx';
import { AlertIcon, ArrowIcon, CheckIcon, ChevronIcon, CloseIcon, LayersIcon, SchemaIcon, SearchIcon } from './icons.tsx';
import { Button, Spinner } from './ui.tsx';

// The strip and the window ask the same: one request per search a minute (the cloud keeps the model's entities a minute too)
const kept = new Map<string, { at: number; value: Promise<EntityLayer> }>();
function layerOf(tid: string, env: string, params: Record<string, string>): Promise<EntityLayer> {
  const key = `${tid}|${env}|${JSON.stringify(params)}`;
  const hit = kept.get(key);
  if (hit && Date.now() - hit.at < 60_000) return hit.value;
  const value = Api.entities(tid, new URLSearchParams(params));
  kept.set(key, { at: Date.now(), value });
  value.catch(() => kept.delete(key));
  if (kept.size > 50) kept.delete(kept.keys().next().value!);
  return value;
}

/** A search that could name a thing: some words, not only operators. */
const couldName = (q: string) => {
  const t = q.trim();
  return t.length >= 3 && t.length <= 300 && t.split(/\s+/).some((w) => !/^[-@]/.test(w) && w !== 'OR');
};

function useLayer(q: string): EntityLayer | null {
  const { tenant } = useAuth();
  const { current } = useEnv();
  const tid = tenant?.id ?? '';
  const env = current?.id ?? '';
  const [layer, setLayer] = useState<{ key: string; value: EntityLayer } | null>(null);
  const key = `${tid}|${env}|${q}`;
  const wanted = !!tid && couldName(q);
  useEffect(() => {
    if (!wanted) return;
    let live = true;
    layerOf(tid, env, { q }).then((value) => { if (live) setLayer({ key, value }); }, () => undefined); // no strip when the layer does not come
    return () => { live = false; };
  }, [wanted, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return wanted && layer?.key === key ? layer.value : null;
}

export function useEntityWindow() {
  return useUrlModal(ENTITY_KEY, (p) => p.get(ENTITY_KEY) ?? undefined, writeEntity);
}

// ---------------------------------------------------------------- the strip

export function EntityStrip({ q }: { q: string }) {
  const layer = useLayer(q);
  const win = useEntityWindow();
  if (!layer || layer.matches.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 mb-3" data-entity-layer>
      {layer.matches.slice(0, 3).map((m) => <StripRow key={m.model.id} m={m} q={q} onOpen={(e) => win.open(encodeEntity({ model: m.model.id, key: e.key }))} />)}
    </div>
  );
}

function StripRow({ m, q, onOpen }: { m: EntityMatch; q: string; onOpen: (e: LensEntity) => void }) {
  const first = m.entities[0];
  const many = m.total > 1;
  return (
    <button type="button" onClick={() => onOpen(first)} data-entity-strip={m.model.id}
      aria-label={many ? `Open ${count(m.total)} ${pluralModel(m.model.name)} of your data model ${m.model.name}` : `Open ${first.title}, ${m.model.name} of your data model`}
      className={cx('group w-full text-left flex items-center gap-3 min-w-0 px-4 py-2.5 rounded-card bg-n2 ring-1 ring-inset ring-accent/40 cursor-pointer text-small',
        'transition-shadow duration-[var(--t-micro)] hover:ring-accent hover:shadow-node mv-rise')}>
      <LayersIcon size={18} className="text-accent shrink-0" />
      <span className="flex flex-col min-w-0 gap-1">
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="text-n8 shrink-0">{m.model.name}</span>
          <span className="text-n6" aria-hidden="true">·</span>
          <span className="font-semibold text-n10 truncate"><Hit text={first.title} q={q} /></span>
          {many && <span className="text-n8 shrink-0 tabular-nums">+ {count(m.total - 1)} more</span>}
        </span>
        <span className="flex items-center gap-1.5 flex-wrap text-micro text-n8">
          {first.systems.map((s) => <SystemBadge key={s} code={s} size={20} />)}
          <span className="ml-1">{entitySummary(first).map((p, i) => (
            <span key={p} className={cx(i > 0 && 'before:content-["·"] before:mx-1.5 before:text-n6', /difference/.test(p) && 'text-warn font-semibold')}>{p}</span>
          ))}</span>
        </span>
      </span>
      <span className="ml-auto inline-flex items-center gap-1.5 font-semibold text-n10 shrink-0">Open<ArrowIcon size={15} className="transition-transform duration-[var(--t-micro)] group-hover:translate-x-0.5" /></span>
    </button>
  );
}

/** The searched words marked in a name, lightly (the strip is not a find). */
function Hit({ text, q }: { text: string; q: string }) {
  const words = q.split(/\s+/).filter((w) => w.length >= 2 && !/^[-@]/.test(w)).map((w) => w.replace(/^"|"$/g, ''));
  let parts = [{ text, hit: false }];
  for (const w of words) parts = parts.flatMap((p) => (p.hit ? [p] : highlightParts(p.text, w)));
  return <>{parts.map((p, i) => (p.hit ? <mark key={i} className="bg-transparent text-inherit underline decoration-accent decoration-2 underline-offset-4">{p.text}</mark> : p.text))}</>;
}

// ---------------------------------------------------------------- the window

export function EntityWindow({ q, onSearch }: { q: string; onSearch: (text: string) => void }) {
  const { tenant } = useAuth();
  const { current, link } = useEnv();
  const tid = tenant?.id ?? '';
  const env = current?.id ?? '';
  const win = useEntityWindow();
  const [params] = useSearchParams();
  const ref = readEntity(params);
  const [layer, setLayer] = useState<{ key: string; value: EntityLayer | null; error?: string } | null>(null);
  const key = ref ? `${tid}|${env}|${q}|${ref.model}|${ref.key}` : '';

  useEffect(() => {
    if (!ref || !tid) return;
    let live = true;
    // The search's own list first (prev/next go through it); an entity it does not have (a link) is asked by its key
    const bySearch = couldName(q) ? layerOf(tid, env, { q }) : Promise.resolve(null);
    bySearch.then(async (l) => {
      const m = l?.matches.find((x) => x.model.id === ref.model);
      if (m?.entities.some((e) => sameKey(e.key, ref.key))) return l;
      return layerOf(tid, env, { model: ref.model, key: ref.key });
    }).then((value) => { if (live) setLayer({ key, value }); }, (e) => { if (live) setLayer({ key, value: null, error: e instanceof Error ? e.message : String(e) }); });
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ref) return null;
  const ready = layer?.key === key ? layer : null;
  const match = ready?.value?.matches.find((x) => x.model.id === ref.model);
  const index = match ? match.entities.findIndex((e) => sameKey(e.key, ref.key)) : -1;
  const entity = index >= 0 ? match!.entities[index] : undefined;
  // Another entity of the same search is a step in history: Back returns to the one before, closing goes back past them all
  const step = (d: number) => {
    if (!match) return;
    const next = match.entities[(index + d + match.entities.length) % match.entities.length];
    win.step((p) => writeEntityView(writeEntity(p, encodeEntity({ model: ref.model, key: next.key })), { systems: null }));
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) win.close(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby={undefined} data-entity-window
          onKeyDown={(e) => {
            if (!match || match.entities.length < 2 || (e.target as HTMLElement).closest('input,textarea')) return;
            if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
          }}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(1100px,94vw)] max-h-[88vh] flex flex-col gap-4 bg-n2 text-n10 rounded-card shadow-float p-5 outline-none mv-pop">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <span className="text-micro text-n8 flex items-center gap-1.5">
                <LayersIcon size={14} className="text-accent" />
                {match ? <>{match.model.name} · your data model · v{match.model.version}{current ? ` in ${current.name}` : ''}</> : 'Your data model'}
              </span>
              <Dialog.Title className="m-0 text-title font-semibold leading-tight [overflow-wrap:anywhere]">{entity?.title ?? ref.key}</Dialog.Title>
              {entity && match && (
                <span className="text-small text-n8">
                  {match.key} <span className="font-mono text-n10">{entity.key}</span>
                  <span className="text-n6 mx-1.5" aria-hidden="true">·</span>{entitySummary(entity).join(' · ')}
                </span>
              )}
            </div>
            {match && match.entities.length > 1 && index >= 0 && (
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-micro text-n8 tabular-nums mr-1" data-entity-position>{index + 1} / {match.entities.length}{match.total > match.entities.length ? ` of ${count(match.total)}` : ''}</span>
                <IconButton label="Previous" onClick={() => step(-1)}><ChevronIcon size={16} className="rotate-90" /></IconButton>
                <IconButton label="Next" onClick={() => step(1)}><ChevronIcon size={16} className="-rotate-90" /></IconButton>
              </div>
            )}
            <Dialog.Close asChild><IconButton label="Close"><CloseIcon size={16} /></IconButton></Dialog.Close>
          </div>

          {!ready && <div className="py-10 grid place-items-center"><Spinner /></div>}
          {ready?.error && <p className="m-0 text-small text-fail">{ready.error}</p>}
          {ready && !ready.error && !entity && (
            <p className="m-0 text-small text-n8">{ref.key} is not in this data model in {current?.name ?? 'this environment'}, or the model is not deployed here.</p>
          )}
          {ready?.value?.partial && <p className="m-0 text-small text-warn">Not all Vaults answered: {ready.value.vaultErrors.map((e) => e.vaultName).join(', ')}.</p>}
          {entity && match && <Matrix entity={entity} match={match} />}

          {entity && match && (
            <div className="flex items-center gap-2 justify-end flex-wrap pt-1">
              <Link to={link(`/settings/model?${new URLSearchParams({ tab: 'yours', model: match.model.id })}`)} className="no-underline">
                <Button><SchemaIcon size={15} />Open the data model</Button>
              </Link>
              <Button variant="primary" onClick={() => onSearch(entitySearch(entity.key, entity.cells[match.key]))}>
                <SearchIcon size={15} />Search everything about this
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The entity's fields in one value column: a value the systems share once with their badges, a difference per value, tags for the rest. */
function Matrix({ entity, match }: { entity: LensEntity; match: EntityMatch }) {
  const [params] = useSearchParams();
  const win = useEntityWindow();
  const view = readEntityView(params);
  const labels = useMemo(() => match.fields.map((f) => f.label), [match.fields]);
  const { updated } = useMemo(() => entityMatrix(entity, labels), [entity, labels]);
  const shown = view.systems ? entity.systems.filter((s) => view.systems!.includes(s)) : entity.systems;
  const rows = useMemo(() => entityRows(entity, labels, shown), [entity, labels, shown.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => Object.fromEntries(ROW_FILTERS.map((f) => [f, rows.filter((r) => rowMatches(r, f)).length])) as Record<RowFilter, number>, [rows]);
  const visible = rows.filter((r) => rowMatches(r, view.rows));
  // The filters are steps in history: Back undoes the last one, closing the window goes back past them all
  const change = (v: Parameters<typeof writeEntityView>[1]) => win.step((p) => writeEntityView(p, v));
  const toggle = (s: string) => {
    const next = shown.includes(s) ? shown.filter((x) => x !== s) : entity.systems.filter((x) => x === s || shown.includes(x));
    if (next.length === 0) return;
    change({ systems: next.length === entity.systems.length ? null : next });
  };
  const ROW_LABEL: Record<RowFilter, string> = { all: 'All fields', differs: 'Differs', missing: 'Missing somewhere', same: 'Same everywhere' };

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
        <div role="group" aria-label="Systems shown" className="flex items-center gap-1.5 flex-wrap" data-entity-systems>
          <span className="text-micro text-n7 uppercase tracking-wide font-semibold mr-1">Systems</span>
          {entity.systems.map((s) => (
            <FacetChip key={s} chosen={shown.includes(s)} onClick={() => toggle(s)} data-key={s}
              title={shown.includes(s) && shown.length === 1 ? 'At least one system is shown' : `${shown.includes(s) ? 'Hide' : 'Show'} the values of ${s}${updated[s] ? ` · updated ${dateTime(updated[s])}` : ''}`}>
              <SystemBadge code={s} size={20} />
              <span className="text-micro text-n8">{updated[s] ? ago(updated[s]) : 'no values'}</span>
            </FacetChip>
          ))}
        </div>
        <Segmented ariaLabel="Fields shown" className="ml-auto" value={view.rows} onChange={(rows) => change({ rows })}
          options={ROW_FILTERS.map((f) => [f, f === 'all' ? ROW_LABEL[f] : `${ROW_LABEL[f]} ${counts[f]}`] as const)} />
      </div>

      <div className="overflow-auto min-h-0 -mx-1 px-1">
        <table className="w-full border-collapse text-small" style={{ tableLayout: 'fixed' }}>
          <colgroup><col style={{ width: 220 }} /><col /><col style={{ width: 230 }} /></colgroup>
          <thead>
            <tr className="text-micro text-n8">
              <th className="text-left font-semibold py-2 pr-3 border-b border-n4">Field</th>
              <th className="text-left font-semibold py-2 px-3 border-b border-n4">Value</th>
              <th className="text-left font-semibold py-2 pl-3 border-b border-n4">Across systems</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={3} className="py-6 text-center text-n8">No field is {ROW_LABEL[view.rows].toLowerCase()} in {shown.join(', ')}.</td></tr>
            )}
            {visible.map((r) => (
              <tr key={r.label} data-entity-row={r.label} data-status={r.status} className={cx(r.status === 'differs' && 'bg-warn-soft/50')}>
                <td className={cx('py-2.5 pr-3 align-top border-b border-n3', r.status === 'differs' && 'pl-2 border-l-[3px] border-l-warn')}>
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className={cx('font-semibold', r.status === 'empty' ? 'text-n7' : 'text-n10')}>{r.label}</span>
                    {r.label === match.key && <span className="text-micro text-n8 rounded-full bg-n3 px-1.5">key</span>}
                  </span>
                </td>
                <td className="py-2.5 px-3 align-top border-b border-n3">
                  {r.groups.length === 0 ? <span className="text-n6">—</span> : (
                    <span className="flex flex-col gap-1.5">
                      {r.groups.map((g) => (
                        <span key={g.systems.join(',')} data-value-group={g.systems.join(',')} className="flex items-start gap-2 min-w-0"
                          title={`${g.systems.join(', ')} · ${dateTime(g.at)}`}>
                          <span className={cx('leading-5 min-w-0 [overflow-wrap:anywhere]', r.status === 'differs' && 'font-semibold text-n10')}>{g.value}</span>
                          {(shown.length > 1) && (
                            <span className="flex items-center gap-1 shrink-0">{g.systems.map((s) => <SystemBadge key={s} code={s} size={20} />)}</span>
                          )}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td className="py-2.5 pl-3 align-top border-b border-n3">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <RowTag r={r} shown={shown} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const tag = 'inline-flex items-center gap-1 h-6 px-2 rounded-full text-micro whitespace-nowrap';

function RowTag({ r, shown }: { r: EntityRow; shown: string[] }) {
  const out = [];
  if (r.status === 'same') out.push(<span key="s" className={cx(tag, 'bg-ok-soft text-ok font-medium')} data-tag="same"><CheckIcon size={12} />Same in {r.groups[0].systems.length}</span>);
  if (r.status === 'differs') out.push(<span key="d" className={cx(tag, 'bg-warn-soft text-warn font-semibold')} data-tag="differs"><AlertIcon size={12} />{r.groups.length} different values</span>);
  if (r.status === 'single' && shown.length > 1) out.push(<span key="o" className={cx(tag, 'bg-n3 text-n9')} data-tag="single">Only in {r.groups[0].systems[0]}</span>);
  if (r.status === 'empty') out.push(<span key="e" className={cx(tag, 'bg-n3 text-n7')} data-tag="empty">No value{shown.length > 1 ? ' in any' : ''}</span>);
  else if (r.missing.length > 0 && r.status !== 'single') {
    out.push(<span key="m" className={cx(tag, 'border border-dashed border-n6 text-n8')} data-tag="missing" title={`No value in ${r.missing.join(', ')}`}>Missing in {r.missing.join(', ')}</span>);
  }
  return <>{out}</>;
}
